'use client';

import { Button, Table, TableBody, TableCell, TableHead, TableHeadCell, TableRow, TextInput } from 'flowbite-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiPencil, HiPlus, HiTrash } from 'react-icons/hi2';
import { toast } from 'react-toastify';
import { ConfirmDialog } from '../../components/ConfirmDialog.tsx';
import { toastError } from '../../components/Toast.tsx';
import { useAppSelector } from '../../lib/hooks.ts';
import { BuildingView, useDeleteRoomMutation, useRenameRoomMutation, useUpsertRoomMutation } from '../../lib/model/kantoordagApi.ts';
import { userHasRole } from '../../lib/user-has-role.tsx';
import { ROOM__DEFAULT_ROOMS } from '../Constants.tsx';

interface RoomListProps {
    building: BuildingView;
    onChanged: () => void;
}

export default function RoomList({ building, onChanged }: RoomListProps) {
    const { t } = useTranslation('building-admin');
    const user = useAppSelector(state => state.user.user);
    // Deleting a room cascades (evicts occupants) and orphans the old name's data on delete --
    // ADMIN alone is refused by the backend (Roles.kt), so hide the trigger here too rather than
    // let a plain admin hit a 403 after confirming.
    const canDeleteRoom = userHasRole(user, ['SUPER_ADMIN']);

    const rooms = building.rooms.filter(room => !ROOM__DEFAULT_ROOMS.includes(room.name));

    const [upsertRoom, upsertRoomResult] = useUpsertRoomMutation();
    const [renameRoom, renameRoomResult] = useRenameRoomMutation();
    const [deleteRoom, deleteRoomResult] = useDeleteRoomMutation();

    const [renamingRoom, setRenamingRoom] = useState<string | undefined>(undefined);
    const [newRoomName, setNewRoomName] = useState('');

    const [resizingRoom, setResizingRoom] = useState<string | undefined>(undefined);
    const [newRoomSeats, setNewRoomSeats] = useState('');

    const [roomToDelete, setRoomToDelete] = useState<string | undefined>(undefined);

    const [addingRoomName, setAddingRoomName] = useState('');
    const [addingRoomSeats, setAddingRoomSeats] = useState('');

    function startRename(room: string) {
        setRenamingRoom(room);
        setNewRoomName(room);
    }

    function confirmRename(oldName: string) {
        const trimmed = newRoomName.trim();
        if (trimmed === '' || trimmed === oldName) {
            setRenamingRoom(undefined);
            return;
        }
        renameRoom({ building: building.name, room: oldName, roomNameUpdate: { newName: trimmed } })
            .unwrap()
            .then(() => {
                toast.success(t('room_renamed'));
                setRenamingRoom(undefined);
                onChanged();
            })
            .catch(() => toastError({ api: renameRoomResult, message: t('room_rename_failed') }));
    }

    function startResize(room: string, seats: number) {
        setResizingRoom(room);
        setNewRoomSeats(String(seats));
    }

    function confirmResize(room: string) {
        const seats = Number(newRoomSeats);
        if (!Number.isInteger(seats) || seats < 0) {
            return;
        }
        upsertRoom({ building: building.name, room, roomSize: { size: seats } })
            .unwrap()
            .then(() => {
                toast.success(t('room_resized'));
                setResizingRoom(undefined);
                onChanged();
            })
            .catch(() => toastError({ api: upsertRoomResult, message: t('room_resize_failed') }));
    }

    function confirmDelete() {
        if (roomToDelete == null) {
            return;
        }
        deleteRoom({ building: building.name, room: roomToDelete })
            .unwrap()
            .then(() => {
                toast.success(t('room_deleted'));
                setRoomToDelete(undefined);
                onChanged();
            })
            .catch(() => {
                setRoomToDelete(undefined);
                toastError({ api: deleteRoomResult, message: t('room_delete_failed') });
            });
    }

    function addRoom() {
        const name = addingRoomName.trim();
        const seats = Number(addingRoomSeats);
        if (name === '' || !Number.isInteger(seats) || seats < 0) {
            return;
        }
        upsertRoom({ building: building.name, room: name, roomSize: { size: seats } })
            .unwrap()
            .then(() => {
                toast.success(t('room_added'));
                setAddingRoomName('');
                setAddingRoomSeats('');
                onChanged();
            })
            .catch(() => toastError({ api: upsertRoomResult, message: t('room_add_failed') }));
    }

    return (
        <div data-testid="room-list">
            <h2 className="mb-2 text-lg font-semibold">{t('rooms_title')}</h2>

            <Table>
                <TableHead>
                    <TableRow>
                        <TableHeadCell>{t('room_name')}</TableHeadCell>
                        <TableHeadCell>{t('room_seats')}</TableHeadCell>
                        <TableHeadCell/>
                    </TableRow>
                </TableHead>
                <TableBody className="divide-y">
                    {rooms.map(room => (
                        <TableRow key={room.name}>
                            <TableCell>
                                {renamingRoom === room.name ? (
                                    <div className="flex items-center gap-2">
                                        <TextInput
                                            sizing="sm"
                                            value={newRoomName}
                                            onChange={event => setNewRoomName(event.target.value)}
                                        />
                                        <Button size="xs" onClick={() => confirmRename(room.name)}>{t('save')}</Button>
                                        <Button size="xs" color="secondary" onClick={() => setRenamingRoom(undefined)}>{t('cancel')}</Button>
                                    </div>
                                ) : (
                                    room.name
                                )}
                            </TableCell>
                            <TableCell>
                                {resizingRoom === room.name ? (
                                    <div className="flex items-center gap-2">
                                        <TextInput
                                            sizing="sm"
                                            type="number"
                                            min={0}
                                            className="w-24"
                                            value={newRoomSeats}
                                            onChange={event => setNewRoomSeats(event.target.value)}
                                        />
                                        <Button size="xs" onClick={() => confirmResize(room.name)}>{t('save')}</Button>
                                        <Button size="xs" color="secondary" onClick={() => setResizingRoom(undefined)}>{t('cancel')}</Button>
                                    </div>
                                ) : (
                                    room.seats
                                )}
                            </TableCell>
                            <TableCell>
                                <div className="flex justify-end gap-2">
                                    <Button
                                        size="xs"
                                        color="secondary"
                                        aria-label={t('rename_room')}
                                        onClick={() => startRename(room.name)}
                                    >
                                        <HiPencil/>
                                    </Button>
                                    <Button
                                        size="xs"
                                        color="secondary"
                                        aria-label={t('resize_room')}
                                        onClick={() => startResize(room.name, room.seats)}
                                    >
                                        {t('resize_room_short')}
                                    </Button>
                                    {canDeleteRoom && (
                                        <Button
                                            size="xs"
                                            color="failure"
                                            aria-label={t('delete_room')}
                                            onClick={() => setRoomToDelete(room.name)}
                                        >
                                            <HiTrash/>
                                        </Button>
                                    )}
                                </div>
                            </TableCell>
                        </TableRow>
                    ))}
                    <TableRow>
                        <TableCell>
                            <TextInput
                                sizing="sm"
                                placeholder={t('new_room_name')}
                                value={addingRoomName}
                                onChange={event => setAddingRoomName(event.target.value)}
                            />
                        </TableCell>
                        <TableCell>
                            <TextInput
                                sizing="sm"
                                type="number"
                                min={0}
                                className="w-24"
                                placeholder={t('new_room_seats')}
                                value={addingRoomSeats}
                                onChange={event => setAddingRoomSeats(event.target.value)}
                            />
                        </TableCell>
                        <TableCell>
                            <div className="flex justify-end">
                                <Button size="xs" onClick={addRoom}>
                                    <HiPlus className="mr-1"/>
                                    {t('add_room')}
                                </Button>
                            </div>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>

            <ConfirmDialog
                message={t('confirm_delete_room', { room: roomToDelete })}
                yes={t('confirm_yes')}
                no={t('confirm_no')}
                show={roomToDelete != null}
                onYes={confirmDelete}
                onNo={() => setRoomToDelete(undefined)}
            />
        </div>
    );
}
